import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { dropdownVariants, cardHover } from '../../utils/animations';
import Skeleton, { SkeletonStatCard } from '../ui/Skeleton';
import { Empty, PageHeader, Pill } from '../ui/kit';
import { MARKS } from '../../lib/marks';

// Hook to track previous value (for comparing what items existed before)
function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

interface Website {
  id: string;
  domain: string;
  netlifyUrl?: string;
  currentActiveUrl?: string;
  websiteStatus?: 'temporary' | 'transitioning' | 'live';
  screenshot?: string;
  status: 'active' | 'setting_up' | 'suspended';
  contentSetupComplete?: boolean;
  businessName?: string;
  trade?: string;
  createdAt: string;
  lastUpdated: string;
  leadsCount?: number;
  newLeadsCount?: number;
  reviewsCount?: number;
  avgRating?: number;
}

type FilterType = 'all' | 'live' | 'needs_setup';
type ViewType = 'grid' | 'list';

const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays <= 6) return `${diffInDays} days ago`;
  if (diffInDays <= 13) return '1 week ago';
  if (diffInDays <= 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return date.toLocaleDateString();
};

const WebsitesPage: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>('all');
  const [sortBy, setSortBy] = useState<'updated' | 'name' | 'created'>('updated');
  const [viewType, setViewType] = useState<ViewType>('grid');
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const { data: websites, isLoading, error, refetch } = useQuery({
    queryKey: ['websites'],
    queryFn: async () => {
      const response = await axios.get('/api/websites');
      return response.data.data.websites as Website[];
    },
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Filter and sort websites
  const filteredWebsites = useMemo(() => {
    if (!websites) return [];

    let result = [...websites];

    // Apply status filter
    if (filter === 'live') {
      result = result.filter(w => w.status === 'active' && w.contentSetupComplete);
    } else if (filter === 'needs_setup') {
      result = result.filter(w => !w.contentSetupComplete || w.status === 'setting_up');
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.businessName || a.domain).localeCompare(b.businessName || b.domain);
        case 'created':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'updated':
        default:
          return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
      }
    });

    return result;
  }, [websites, filter, sortBy]);

  // Count for filter tabs
  const counts = useMemo(() => {
    if (!websites) return { all: 0, live: 0, needsSetup: 0 };
    return {
      all: websites.length,
      live: websites.filter(w => w.status === 'active' && w.contentSetupComplete).length,
      needsSetup: websites.filter(w => !w.contentSetupComplete || w.status === 'setting_up').length,
    };
  }, [websites]);

  // Track which website IDs existed in the previous render
  // This lets us animate only truly NEW items, not items that were just filtered back in
  const websiteIds = filteredWebsites.map(w => w.id);
  const previousWebsiteIds = usePrevious(websiteIds);
  const previousIdSet = new Set(previousWebsiteIds || []);

  // Single website redirect - go to Dashboard (which has the expanded single website view)
  useEffect(() => {
    if (!isLoading && websites && websites.length === 1) {
      navigate('/dashboard', { replace: true });
    }
  }, [websites, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton variant="text" width={180} height={32} className="mb-2" />
          <Skeleton variant="text" width={220} height={16} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <Skeleton variant="rectangular" height={144} className="w-full" />
              <div className="p-4 space-y-3">
                <Skeleton variant="text" width="70%" height={20} />
                <Skeleton variant="text" width="50%" height={14} />
                <div className="pt-3 border-t border-zinc-100">
                  <Skeleton variant="rounded" width={120} height={36} className="ml-auto" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-red-50 border border-red-100 rounded-xl p-6"
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-red-900">Error loading websites</h3>
            <p className="text-sm text-red-700 mt-1">Unable to load your websites. Please try again.</p>
            <motion.button
              onClick={() => refetch()}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="mt-3 text-sm font-medium text-red-700 hover:text-red-800"
            >
              Try again
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        eyebrow={
          websites && websites.length > 1
            ? `${websites.length} websites in your account`
            : 'Your account'
        }
        title={websites?.length === 1 ? 'Your website' : 'Websites'}
        actions={
          <Pill tone="outline" size="sm" onClick={() => refetch()}>
            <RefreshIcon className="w-4 h-4" />
            Refresh
          </Pill>
        }
      />

      {/* Toolbar - Pill dropdown filters (only show if multiple websites) */}
      {websites && websites.length > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Filter Dropdowns */}
          <div className="flex items-center gap-2">
            {/* Status Filter */}
            <div className="relative">
              <motion.button
                onClick={() => { setFilterOpen(!filterOpen); setSortOpen(false); }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full border transition-all ${
                  filter !== 'all'
                    ? 'bg-primary border-primary text-white'
                    : 'bg-white/60 border-zinc-300 text-primary hover:bg-white'
                }`}
              >
                {filter === 'all' && 'All websites'}
                {filter === 'live' && 'Live'}
                {filter === 'needs_setup' && 'Needs setup'}
                <motion.span animate={{ rotate: filterOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDownIcon className="w-4 h-4" />
                </motion.span>
              </motion.button>
              <AnimatePresence>
                {filterOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setFilterOpen(false)} />
                    <motion.div
                      variants={dropdownVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      className="absolute left-0 mt-1 bg-white border border-zinc-200 rounded-xl z-20 min-w-[160px] overflow-hidden"
                    >
                      <button
                        onClick={() => { setFilter('all'); setFilterOpen(false); }}
                        className={`w-full px-4 py-2.5 text-sm text-left transition-colors ${filter === 'all' ? 'bg-paper-deep text-primary font-medium' : 'text-primary hover:bg-paper'}`}
                      >
                        All websites
                      </button>
                      <button
                        onClick={() => { setFilter('live'); setFilterOpen(false); }}
                        className={`w-full px-4 py-2.5 text-sm text-left transition-colors ${filter === 'live' ? 'bg-paper-deep text-primary font-medium' : 'text-primary hover:bg-paper'}`}
                      >
                        Live
                      </button>
                      <button
                        onClick={() => { setFilter('needs_setup'); setFilterOpen(false); }}
                        className={`w-full px-4 py-2.5 text-sm text-left transition-colors ${filter === 'needs_setup' ? 'bg-paper-deep text-primary font-medium' : 'text-primary hover:bg-paper'}`}
                      >
                        Needs setup
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            {/* Sort Filter */}
            <div className="relative">
              <motion.button
                onClick={() => { setSortOpen(!sortOpen); setFilterOpen(false); }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full border border-zinc-200 bg-white text-primary hover:bg-zinc-100 transition-all"
              >
                {sortBy === 'updated' && 'Last updated'}
                {sortBy === 'name' && 'Name'}
                {sortBy === 'created' && 'Date created'}
                <motion.span animate={{ rotate: sortOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDownIcon className="w-4 h-4" />
                </motion.span>
              </motion.button>
              <AnimatePresence>
                {sortOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setSortOpen(false)} />
                    <motion.div
                      variants={dropdownVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      className="absolute left-0 mt-1 bg-white border border-zinc-200 rounded-xl z-20 min-w-[140px] overflow-hidden"
                    >
                      <button
                        onClick={() => { setSortBy('updated'); setSortOpen(false); }}
                        className={`w-full px-4 py-2.5 text-sm text-left transition-colors ${sortBy === 'updated' ? 'bg-paper-deep text-primary font-medium' : 'text-primary hover:bg-paper'}`}
                      >
                        Last updated
                      </button>
                      <button
                        onClick={() => { setSortBy('name'); setSortOpen(false); }}
                        className={`w-full px-4 py-2.5 text-sm text-left transition-colors ${sortBy === 'name' ? 'bg-paper-deep text-primary font-medium' : 'text-primary hover:bg-paper'}`}
                      >
                        Name
                      </button>
                      <button
                        onClick={() => { setSortBy('created'); setSortOpen(false); }}
                        className={`w-full px-4 py-2.5 text-sm text-left transition-colors ${sortBy === 'created' ? 'bg-paper-deep text-primary font-medium' : 'text-primary hover:bg-paper'}`}
                      >
                        Date created
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-1">
            <motion.button
              onClick={() => setViewType('grid')}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className={`p-2 rounded-lg transition-colors ${viewType === 'grid' ? 'bg-zinc-100 text-primary' : 'text-muted hover:bg-zinc-100'}`}
              title="Grid view"
            >
              <GridIcon className="w-5 h-5" />
            </motion.button>
            <motion.button
              onClick={() => setViewType('list')}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className={`p-2 rounded-lg transition-colors ${viewType === 'list' ? 'bg-zinc-100 text-primary' : 'text-muted hover:bg-zinc-100'}`}
              title="List view"
            >
              <ListIcon className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      )}

      {/* Website Grid/List */}
      {filteredWebsites.length > 0 ? (
        viewType === 'grid' || (websites?.length === 1) ? (
          <motion.div
            className={`grid gap-6 ${
              websites?.length === 1
                ? 'grid-cols-1 max-w-lg'
                : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
            }`}
          >
            {filteredWebsites.map((website, index) => {
              // Only animate items that are truly new (didn't exist in previous render)
              const isExistingItem = previousIdSet.has(website.id);
              return (
                <WebsiteCard key={website.id} website={website} isSingleWebsite={websites?.length === 1} index={index} skipAnimation={isExistingItem} />
              );
            })}
          </motion.div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            {/* Column Headers */}
            <div className="hidden md:grid md:grid-cols-[minmax(120px,1fr)_75px_50px_55px_85px_180px] items-center gap-4 px-4 py-3 bg-zinc-50 border-b border-zinc-200 text-xs font-medium text-muted uppercase tracking-wider">
              <div>Website</div>
              <div className="text-center">Status</div>
              <div className="text-center">Leads</div>
              <div className="text-center">Rating</div>
              <div className="text-center">Updated</div>
              <div className="text-right">Actions</div>
            </div>
            {filteredWebsites.map((website, index) => {
              // Only animate items that are truly new (didn't exist in previous render)
              const isExistingItem = previousIdSet.has(website.id);
              return (
                <WebsiteListItem
                  key={website.id}
                  website={website}
                  isLast={index === filteredWebsites.length - 1}
                  index={index}
                  skipAnimation={isExistingItem}
                />
              );
            })}
          </div>
        )
      ) : websites && websites.length > 0 ? (
        // No results from filter
        <motion.div
          className="text-center py-12 bg-white border border-zinc-200 rounded-xl"
        >
          <GlobeIcon className="w-12 h-12 text-zinc-300 mx-auto" />
          <h3 className="mt-4 text-lg font-semibold text-primary">No websites found</h3>
          <p className="text-muted mt-1">Try adjusting your filter</p>
          <motion.button
            onClick={() => setFilter('all')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="mt-4 text-[13px] font-medium text-primary underline-offset-4 hover:underline"
          >
            Show all websites
          </motion.button>
        </motion.div>
      ) : (
        <EmptyState />
      )}
    </div>
  );
};

// Website Card Component (Grid View)
interface WebsiteCardProps {
  website: Website;
  isSingleWebsite?: boolean;
  index?: number;
  skipAnimation?: boolean;
}

const WebsiteCard: React.FC<WebsiteCardProps> = ({ website, isSingleWebsite, index = 0, skipAnimation }) => {
  const needsSetup = !website.contentSetupComplete;
  const isLive = website.status === 'active' && website.contentSetupComplete;

  const getTradeIcon = () => {
    switch (website.trade) {
      case 'plumbing': return '🔧';
      case 'hvac': return '❄️';
      case 'roofing': return '🏠';
      case 'electrical': return '⚡';
      case 'landscaping': return '🌿';
      default: return null;
    }
  };

  return (
    <motion.div
      initial={skipAnimation ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={skipAnimation ? { duration: 0 } : { delay: index * 0.1, duration: 0.3 }}
      whileHover={{ y: -4, boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)' }}
      className="bg-white border border-zinc-200 rounded-xl overflow-hidden flex flex-col"
    >
      {/* Screenshot/Preview */}
      <div className="aspect-[16/10] bg-zinc-100 relative flex-shrink-0">
        {website.screenshot ? (
          <img
            src={website.screenshot}
            alt={website.domain}
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-paper-deep">
            {needsSetup ? (
              <div className="text-center">
                <div className="w-10 h-10 bg-zinc-100 rounded-xl flex items-center justify-center mx-auto">
                  <AlertIcon className="w-5 h-5 text-amber-500" />
                </div>
                <p className="text-xs text-zinc-400 mt-2">Setup required</p>
              </div>
            ) : getTradeIcon() ? (
              <span className="text-3xl">{getTradeIcon()}</span>
            ) : (
              <GlobeIcon className="w-8 h-8 text-zinc-300" />
            )}
          </div>
        )}

        {/* Status Badge */}
        <div className="absolute top-3 left-3">
          {needsSetup ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500 text-white shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-white/60" />
              Setup
            </span>
          ) : isLive ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500 text-white shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse" />
              Live
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-500 text-white shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-white/60" />
              Pending
            </span>
          )}
        </div>

        {/* Visit Site - Top Right */}
        {isLive && (
          <a
            href={website.currentActiveUrl?.startsWith('http') ? website.currentActiveUrl : `https://${website.currentActiveUrl || website.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute top-3 right-3 p-1.5 bg-white/90 hover:bg-white rounded-lg shadow-sm transition-colors"
            title="Visit site"
          >
            <ExternalLinkIcon className="w-4 h-4 text-zinc-600" />
          </a>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        {/* Title & Domain */}
        <div>
          <h3 className="font-semibold text-primary truncate">
            {website.businessName || website.domain}
          </h3>
          <p className="text-xs text-muted truncate mt-0.5">
            {website.domain}
          </p>
        </div>

        {/* Stats - Material/Google style */}
        {isLive && (
          <div className="mt-3 pt-3 border-t border-zinc-100 space-y-1">
            <Link
              to={`/dashboard/websites/${website.id}/stats`}
              className="block text-sm text-muted hover:text-primary transition-colors"
            >
              <span className="font-medium text-primary">127</span> visitors today
            </Link>
            <Link
              to={`/dashboard/leads?website=${website.id}`}
              className="block text-sm text-muted hover:text-primary transition-colors"
            >
              <span className="font-medium text-primary">{website.leadsCount || 0}</span> leads
              {(website.newLeadsCount || 0) > 0 && (
                <span className="ml-1 text-xs text-green-600">+{website.newLeadsCount} new</span>
              )}
            </Link>
            <Link
              to={`/dashboard/reviews?website=${website.id}`}
              className="block text-sm text-muted hover:text-primary transition-colors"
            >
              <span className="font-medium text-primary">{website.avgRating ? website.avgRating.toFixed(1) : '—'}</span>
              <span className="text-amber-500">★</span> rating
            </Link>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Action Button - Right aligned, compact */}
        <div className="mt-3 pt-3 border-t border-zinc-100 flex justify-end">
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Link
              to={`/dashboard/websites/${website.id}/edit`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                needsSetup
                  ? 'bg-zinc-500 hover:bg-zinc-600 text-white'
                  : 'bg-primary text-white hover:scale-[1.02]'
              }`}
            >
              {needsSetup ? 'Complete Setup' : 'Add Content'}
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

// Website List Item Component (List View)
interface WebsiteListItemProps {
  website: Website;
  isLast: boolean;
  index?: number;
  skipAnimation?: boolean;
}

const WebsiteListItem: React.FC<WebsiteListItemProps> = ({ website, isLast, index = 0, skipAnimation }) => {
  const needsSetup = !website.contentSetupComplete;
  const isLive = website.status === 'active' && website.contentSetupComplete;

  const getTradeIcon = () => {
    switch (website.trade) {
      case 'plumbing': return '🔧';
      case 'hvac': return '❄️';
      case 'roofing': return '🏠';
      case 'electrical': return '⚡';
      case 'landscaping': return '🌿';
      default: return null;
    }
  };

  return (
    <motion.div
      initial={skipAnimation ? false : { opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={skipAnimation ? { duration: 0 } : { delay: index * 0.05, duration: 0.3 }}
      whileHover={{ backgroundColor: 'rgb(250, 250, 250)' }}
      className={`grid grid-cols-[1fr_auto] md:grid-cols-[minmax(120px,1fr)_75px_50px_55px_85px_180px] items-center gap-4 px-4 py-3 ${!isLast ? 'border-b border-zinc-100' : ''}`}
    >
      {/* Website Info - Thumbnail + Name */}
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-12 h-10 bg-zinc-100 rounded-lg overflow-hidden flex-shrink-0">
          {website.screenshot ? (
            <img
              src={website.screenshot}
              alt={website.domain}
              className="w-full h-full object-cover object-top"
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-paper-deep">
              {getTradeIcon() ? (
                <span className="text-base">{getTradeIcon()}</span>
              ) : (
                <GlobeIcon className="w-4 h-4 text-zinc-300" />
              )}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <h3 className="font-medium text-primary truncate">
            {website.businessName || website.domain}
          </h3>
          <p className="text-xs text-muted truncate">{website.domain}</p>
        </div>
      </div>

      {/* Status */}
      <div className="hidden md:flex justify-center">
        {needsSetup ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-zinc-200 text-zinc-800">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            Setup
          </span>
        ) : isLive ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            Live
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600">
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
            Pending
          </span>
        )}
      </div>

      {/* Leads */}
      <div className="hidden md:flex justify-center">
        {isLive ? (
          <span className="text-sm font-medium text-primary">{website.leadsCount || 0}</span>
        ) : (
          <span className="text-sm text-zinc-300">—</span>
        )}
      </div>

      {/* Rating */}
      <div className="hidden md:flex justify-center items-center gap-1">
        {isLive && website.avgRating ? (
          <>
            <StarIcon className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
            <span className="text-sm font-medium text-primary">{website.avgRating.toFixed(1)}</span>
          </>
        ) : (
          <span className="text-sm text-zinc-300">—</span>
        )}
      </div>

      {/* Updated */}
      <div className="hidden md:flex justify-center overflow-hidden">
        <span className="text-sm text-muted whitespace-nowrap">{formatRelativeDate(website.lastUpdated)}</span>
      </div>

      {/* Actions - Right side */}
      <div className="flex items-center gap-1 justify-end flex-shrink-0 overflow-hidden">
        {isLive && (
          <>
            {/* Analytics - hidden on md, visible on lg+ */}
            <Link
              to={`/dashboard/websites/${website.id}/stats`}
              className="hidden lg:flex p-1.5 text-zinc-400 hover:text-primary hover:bg-zinc-100 rounded-lg transition-colors"
              title="Analytics"
            >
              <ChartIcon className="w-4 h-4" />
            </Link>
            {/* Leads - hidden on md, visible on lg+ */}
            <Link
              to={`/dashboard/leads?website=${website.id}`}
              className="hidden lg:flex p-1.5 text-zinc-400 hover:text-primary hover:bg-zinc-100 rounded-lg transition-colors"
              title="Leads"
            >
              <UsersIcon className="w-4 h-4" />
            </Link>
            {/* Custom Domain - hidden on md, visible on lg+ */}
            <Link
              to={`/dashboard/websites/${website.id}/domain`}
              className="hidden lg:flex p-1.5 text-zinc-400 hover:text-primary hover:bg-zinc-100 rounded-lg transition-colors"
              title="Custom domain"
            >
              <GlobeIcon className="w-4 h-4" />
            </Link>
            {/* Update Website - always visible */}
            <Link
              to={`/dashboard/websites/${website.id}/edit`}
              className="p-1.5 text-zinc-400 hover:text-primary hover:bg-zinc-100 rounded-lg transition-colors"
              title="Update Website"
            >
              <EditIcon className="w-4 h-4" />
            </Link>
            {/* Visit Site - always visible */}
            <a
              href={website.currentActiveUrl?.startsWith('http') ? website.currentActiveUrl : `https://${website.currentActiveUrl || website.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 text-zinc-400 hover:text-primary hover:bg-zinc-100 rounded-lg transition-colors"
              title="Visit site"
            >
              <ExternalLinkIcon className="w-4 h-4" />
            </a>
          </>
        )}

        {/* Primary Action Button */}
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Link
            to={`/dashboard/websites/${website.id}/edit`}
            className={`ml-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
              needsSetup
                ? 'bg-zinc-500 hover:bg-zinc-600 text-white'
                : 'bg-primary text-white hover:scale-[1.02]'
            }`}
          >
            {needsSetup ? 'Setup' : 'Add Content'}
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
};

// Empty State Component
const EmptyState: React.FC = () => {
  return (
    <Empty
      framed
      mark={MARKS.websites}
      eyebrow="Your sites"
      title="No websites yet"
      description="Once your site is built it shows up here, with its traffic, leads and deploy status."
      action={
        <motion.a
          href="https://foundlio.com/contact"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-white transition-transform"
        >
          Get Started
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
          </svg>
        </motion.a>
      }
      hint="Building a site takes about a day; you'll get an email the moment it's live."
    />
  );
};

// Icons
const GlobeIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 12h17M12 3.5c2 2.5 3.5 5.2 3.5 8.5s-1.5 6-3.5 8.5c-2-2.5-3.5-5.2-3.5-8.5s1.5-6 3.5-8.5z" />
  </svg>
);

const RefreshIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const SearchIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
);

const XIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const ChevronDownIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
  </svg>
);

const GridIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" />
  </svg>
);

const ListIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
  </svg>
);

const MoreIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
  </svg>
);

const EditIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" />
  </svg>
);

const ExternalLinkIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 3h6v6" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 14 21 3" />
  </svg>
);

const ChartIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" />
  </svg>
);

const UsersIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
  </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
  </svg>
);

const AlertIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
  </svg>
);

const PlusIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

export default WebsitesPage;
